import React from "react";
import "@/styles/native-standings-v3.css";
import {
  CalendarDays,
  ChevronRight,
  CircleDot,
  Info,
  ListOrdered,
  Shield,
  Trophy,
} from "lucide-react";

import TeamLogo from "@/components/TeamLogo";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAccount } from "@/queries/account";

const PLAYOFF_STEPS = ["Kvartsfinal", "QF-tabell", "Semifinal", "Final"];
const PLACEHOLDER_COUNTS = {
  quarterfinal_series: 3,
  semifinal_series: 2,
  final: 1,
};

const PLAYOFF_INFO = {
  quarterfinal_series: {
    title: "Kvartsfinal",
    description: "Grundseriens sex främsta lag går vidare. Kvartsfinalerna avgörs över två möten och resultaten ligger till grund för den gemensamma kvartsfinaltabellen.",
  },
  quarterfinal_table: {
    title: "Kvartsfinaltabell",
    description: "Lagen rangordnas efter lagpoäng, därefter matchpoängkvot och slutligen placeringen i grundserien. De fyra främsta lagen går vidare till semifinal.",
  },
  semifinal_series: {
    title: "Semifinal",
    description: "Ettan i kvartsfinaltabellen möter fyran och tvåan möter trean. Semifinalerna avgörs över ett hemma- och ett bortamöte.",
  },
  final: {
    title: "Final",
    description: "Semifinalvinnarna möts i två finalmatcher. Laget med högst sammanlagd poäng efter hemma- och bortamötet blir svensk mästare.",
  },
};

function formatDate(value) {
  if (!value) return "Datum ej fastställt";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Datum ej fastställt";
  return new Intl.DateTimeFormat("sv-SE", { day: "numeric", month: "short" }).format(date);
}

function FormDots({ values = [] }) {
  if (!values.length) return <span className="native-v3-form-empty">Ingen form ännu</span>;
  return (
    <div className="native-v3-form-dots" aria-label={`Form: ${values.join(", ")}`}>
      {values.map((value, index) => (
        <span key={`${value}-${index}`} data-result={String(value || "").toLowerCase()}>{value}</span>
      ))}
    </div>
  );
}

function StandingsHeader({ season, activeTab, onTabChange }) {
  return (
    <header className="native-v3-standings-header">
      <div className="native-v3-standings-title-row">
        <h1 className="native-v3-display">{activeTab === "slutspel" ? "Slutspel" : "Tabell"}</h1>
        <span className="native-v3-season-label">Säsong {season}</span>
      </div>
      <div className="native-v3-standings-tabs" role="tablist" aria-label="Tabell eller slutspel">
        <button type="button" role="tab" aria-selected={activeTab === "tabell"} data-active={activeTab === "tabell" ? "true" : "false"} onClick={() => onTabChange("tabell")}>
          <ListOrdered aria-hidden="true" /> Tabell
        </button>
        <button type="button" role="tab" aria-selected={activeTab === "slutspel"} data-active={activeTab === "slutspel" ? "true" : "false"} onClick={() => onTabChange("slutspel")}>
          <Trophy aria-hidden="true" /> Slutspel
        </button>
      </div>
    </header>
  );
}

function SelectedTeam({ team, onOpenTeam }) {
  if (!team) return null;
  return (
    <section className="native-v3-selected-team">
      <div className="native-v3-selected-main">
        <span className="native-v3-selected-rank">#{team.displayPosition ?? team.position}</span>
        <TeamLogo teamName={team.team} size={64} className="native-v3-selected-logo" fallback={<Shield aria-hidden="true" />} />
        <span className="native-v3-selected-name">
          <strong>{team.team}</strong>
          <small>{team.city || "Bauhausligan"}</small>
        </span>
        <span className="native-v3-selected-points">
          <strong>{team.points} <small>poäng</small></strong>
          <span>{team.played} matcher</span>
        </span>
      </div>
      <div className="native-v3-selected-footer">
        <div><span>Form</span><FormDots values={team.form || []} /></div>
        {team.team_id ? (
          <button type="button" onClick={() => onOpenTeam(team.team_id)}>Visa lag <ChevronRight aria-hidden="true" /></button>
        ) : null}
      </div>
    </section>
  );
}

function NativeTable({ rows, loading, error, selectedId, onSelect }) {
  if (loading) {
    return (
      <section className="native-v3-table-section native-v3-standings-table-skeleton" aria-label="Laddar tabell">
        <div className="native-v3-table-head"><span>#</span><span>Lag</span><span>M</span><span>+/-</span><span>P</span></div>
        <div className="native-v3-table-body">
          {Array.from({ length: 8 }).map((_, index) => (
            <Skeleton className="native-v3-table-row-silhouette" key={index} />
          ))}
        </div>
      </section>
    );
  }
  if (error) return <div className="native-v3-standings-message">Kunde inte ladda tabellen.</div>;
  if (!rows.length) return <div className="native-v3-standings-message">Ingen tabelldata ännu.</div>;

  return (
    <section className="native-v3-table-section">
      <div className="native-v3-table-head">
        <span>#</span><span>Lag</span><span>M</span><span>+/-</span><span>P</span>
      </div>
      <div className="native-v3-table-body">
        {rows.map((team) => {
          const id = String(team.team_id || team.team);
          const selected = id === selectedId;
          return (
            <button type="button" key={id} className="native-v3-table-row" data-selected={selected ? "true" : "false"} onClick={() => onSelect(id)}>
              <span className="native-v3-table-position">{team.displayPosition ?? team.position}</span>
              <span className="native-v3-table-team">
                <TeamLogo teamName={team.team} size={30} fallback={<Shield aria-hidden="true" />} />
                <strong>{team.team}</strong>
              </span>
              <span>{team.played}</span>
              <span className={Number(team.points_diff) < 0 ? "native-v3-negative" : ""}>{Number(team.points_diff) > 0 ? `+${team.points_diff}` : team.points_diff}</span>
              <strong>{team.points}</strong>
            </button>
          );
        })}
      </div>
      <div className="native-v3-table-note"><Info aria-hidden="true" /> Bonuspoäng är inkluderade i tabellpoängen.</div>
    </section>
  );
}

function MatchLeg({ match, index }) {
  const hasScore = match?.home_score != null && match?.away_score != null;
  return (
    <div className="native-v3-playoff-leg">
      <div className="native-v3-playoff-leg-meta"><span>Möte {match?.leg || index + 1}</span><small><CalendarDays aria-hidden="true" />{formatDate(match?.date)}</small></div>
      <div className="native-v3-playoff-team-line">
        <span><TeamLogo teamName={match?.home_team} size={28} fallback={<Shield aria-hidden="true" />} /><strong>{match?.home_team || "Lag ej fastställt"}</strong><small className="native-v3-playoff-venue">Hemma</small></span>
        <b>{hasScore ? match.home_score : "-"}</b>
      </div>
      <div className="native-v3-playoff-team-line">
        <span><TeamLogo teamName={match?.away_team} size={28} fallback={<Shield aria-hidden="true" />} /><strong>{match?.away_team || "Lag ej fastställt"}</strong><small className="native-v3-playoff-venue">Borta</small></span>
        <b>{hasScore ? match.away_score : "-"}</b>
      </div>
    </div>
  );
}

function PlaceholderGroup({ label }) {
  return (
    <div className="native-v3-playoff-group native-v3-playoff-placeholder">
      <h3>{label}</h3>
      <div><CircleDot aria-hidden="true" /><span>Lagen fastställs när föregående steg är klart.</span></div>
      <div><span>Möte 1</span><small>Datum ej fastställt</small></div>
      <div><span>Möte 2</span><small>Datum ej fastställt</small></div>
    </div>
  );
}

function getStageStatus(stage) {
  if (stage?.complete) return "Avslutad";
  if ((stage?.groups || []).some((group) => group?.requires_resolution)) return "Inväntar avgörande";
  const matches = (stage?.groups || []).flatMap((group) => group?.matches || []);
  if (!matches.length) return "Ej fastställd";
  if (matches.some((match) => match?.home_score != null || match?.away_score != null)) return "Pågående";
  return "Fastställd";
}

function GroupOutcome({ group, type }) {
  const winner = group?.winner || {};
  if (!winner.matches_complete) return null;

  if (winner.requires_resolution) {
    return (
      <div className="native-v3-playoff-outcome" data-status="pending">
        <CircleDot aria-hidden="true" />
        <span><strong>Sammanlagt {winner.winner_score}–{winner.runner_up_score}</strong><small>Inväntar avgörande efter skiljeheat eller officiellt beslut.</small></span>
      </div>
    );
  }
  if (!winner.winner_team) return null;

  const decidedByRunoff = winner.is_tie && winner.resolution_type === "runoff";
  return (
    <div className="native-v3-playoff-outcome" data-status="resolved">
      <Trophy aria-hidden="true" />
      <span>
        <strong>{winner.winner_team}</strong>
        <small>{type === "final" ? "Svenska mästare" : "Vinnare"}{decidedByRunoff ? " efter skiljeheat" : ` · sammanlagt ${winner.winner_score}–${winner.runner_up_score}`}</small>
      </span>
    </div>
  );
}

function StageHeading({ title, status, onInfo }) {
  return (
    <div className="native-v3-playoff-stage-heading">
      <span>{title}</span>
      <div>
        <small>{status}</small>
        <button type="button" onClick={onInfo} aria-label={`Information om ${title}`}><Info aria-hidden="true" /></button>
      </div>
    </div>
  );
}

function StageGroups({ stage, type, title, onInfo }) {
  const groups = Array.isArray(stage?.groups) ? stage.groups : [];
  const count = PLACEHOLDER_COUNTS[type] || 1;
  return (
    <section className="native-v3-playoff-stage">
      <StageHeading title={title} status={getStageStatus(stage)} onInfo={onInfo} />
      {groups.length ? groups.map((group, groupIndex) => (
        <div className="native-v3-playoff-group" key={group.key || `${type}-${groupIndex}`}>
          <h3>{group.label || `${title} ${groupIndex + 1}`}</h3>
          <div className="native-v3-playoff-legs">
            {(group.matches || []).length
              ? group.matches.map((match, index) => <MatchLeg key={match.id || `${groupIndex}-${index}`} match={match} index={index} />)
              : <PlaceholderGroup label="Matcher ej fastställda" />}
          </div>
          <GroupOutcome group={group} type={type} />
        </div>
      )) : Array.from({ length: count }, (_, index) => <PlaceholderGroup key={`${type}-${index}`} label={`${title} ${count > 1 ? index + 1 : ""}`.trim()} />)}
    </section>
  );
}

function QuarterfinalStandings({ stage, onInfo }) {
  const rows = stage?.standings?.rows || [];
  return (
    <section className="native-v3-playoff-stage">
      <StageHeading
        title="Kvartsfinaltabell"
        status={rows.length ? (stage?.complete ? "Slutlig ställning" : "Aktuell ställning") : "Inväntar resultat"}
        onInfo={onInfo}
      />
      <div className="native-v3-qf-table">
        {rows.length ? rows.map((row) => (
          <div key={row.team_id || row.team}>
            <span>#{row.position}</span>
            <span><TeamLogo teamName={row.team} size={26} /><strong>{row.team}</strong></span>
            <small>{row.for_points ?? 0}–{row.against_points ?? 0}</small>
            <b>{row.points} lp</b>
          </div>
        )) : <p>Tabellen fylls när kvartsfinalmatcherna börjar spelas.</p>}
      </div>
    </section>
  );
}

function PlayoffCanvas({ stages, completedStep, currentStep }) {
  const qf = stages.find((stage) => stage.stage_type === "quarterfinal_series");
  const sf = stages.find((stage) => stage.stage_type === "semifinal_series");
  const final = stages.find((stage) => stage.stage_type === "final");
  const initialStage = Math.min(Math.max(currentStep, 0), PLAYOFF_STEPS.length - 1);
  const [activeStage, setActiveStage] = React.useState(initialStage);
  const [viewportHeight, setViewportHeight] = React.useState(0);
  const railRef = React.useRef(null);
  const panelRefs = React.useRef([]);
  const panelContentRefs = React.useRef([]);
  const scrollFrameRef = React.useRef(null);
  const programmaticTargetRef = React.useRef(null);
  const programmaticTimerRef = React.useRef(null);
  const [infoType, setInfoType] = React.useState(null);

  const panels = [
    <StageGroups key="quarterfinal" stage={qf} type="quarterfinal_series" title="Kvartsfinal" onInfo={() => setInfoType("quarterfinal_series")} />,
    <QuarterfinalStandings key="quarterfinal-table" stage={qf} onInfo={() => setInfoType("quarterfinal_table")} />,
    <StageGroups key="semifinal" stage={sf} type="semifinal_series" title="Semifinal" onInfo={() => setInfoType("semifinal_series")} />,
    <StageGroups key="final" stage={final} type="final" title="Final" onInfo={() => setInfoType("final")} />,
  ];

  const measurePanel = React.useCallback((index) => {
    const content = panelContentRefs.current[index];
    if (!content) return;
    setViewportHeight(Math.ceil(content.getBoundingClientRect().height));
  }, []);

  const scrollToStage = React.useCallback((index, behavior = "smooth") => {
    const rail = railRef.current;
    const panel = panelRefs.current[index];
    if (!rail || !panel) return;
    programmaticTargetRef.current = index;
    if (programmaticTimerRef.current) window.clearTimeout(programmaticTimerRef.current);
    setActiveStage(index);
    window.requestAnimationFrame(() => measurePanel(index));
    rail.scrollTo({ left: panel.offsetLeft, behavior });
    programmaticTimerRef.current = window.setTimeout(() => {
      programmaticTargetRef.current = null;
      programmaticTimerRef.current = null;
    }, behavior === "auto" ? 0 : 520);
  }, [measurePanel]);

  React.useLayoutEffect(() => {
    const frame = window.requestAnimationFrame(() => scrollToStage(initialStage, "auto"));
    return () => window.cancelAnimationFrame(frame);
  }, [initialStage, scrollToStage]);

  React.useEffect(() => {
    const handleResize = () => measurePanel(activeStage);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [activeStage, measurePanel]);

  React.useLayoutEffect(() => {
    const content = panelContentRefs.current[activeStage];
    if (!content) return undefined;
    measurePanel(activeStage);
    if (typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver(() => measurePanel(activeStage));
    observer.observe(content);
    return () => observer.disconnect();
  }, [activeStage, measurePanel]);

  const handleScroll = () => {
    if (scrollFrameRef.current) return;
    scrollFrameRef.current = window.requestAnimationFrame(() => {
      scrollFrameRef.current = null;
      const rail = railRef.current;
      if (!rail) return;
      if (programmaticTargetRef.current != null) {
        measurePanel(programmaticTargetRef.current);
        return;
      }
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;
      panelRefs.current.forEach((panel, index) => {
        if (!panel) return;
        const distance = Math.abs(panel.offsetLeft - rail.scrollLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });
      setActiveStage((current) => current === closestIndex ? current : closestIndex);
      measurePanel(closestIndex);
    });
  };

  React.useEffect(() => () => {
    if (scrollFrameRef.current) window.cancelAnimationFrame(scrollFrameRef.current);
    if (programmaticTimerRef.current) window.clearTimeout(programmaticTimerRef.current);
  }, []);

  return (
    <div className="native-v3-playoffs">
      <div className="native-v3-playoff-progress">
        {PLAYOFF_STEPS.map((label, index) => (
          <button
            type="button"
            key={label}
            data-complete={completedStep >= index + 1 ? "true" : "false"}
            data-active={activeStage === index ? "true" : "false"}
            aria-current={activeStage === index ? "step" : undefined}
            onClick={() => scrollToStage(index)}
          >
            <i>{index + 1}</i><small>{label}</small>
          </button>
        ))}
      </div>
      <div className="native-v3-playoff-viewport" style={viewportHeight ? { height: `${viewportHeight}px` } : undefined}>
        <div ref={railRef} className="native-v3-playoff-canvas" onScroll={handleScroll}>
          {panels.map((panel, index) => (
            <div
              key={PLAYOFF_STEPS[index]}
              ref={(element) => { panelRefs.current[index] = element; }}
              className="native-v3-playoff-panel"
              data-active={activeStage === index ? "true" : "false"}
            >
              <div
                ref={(element) => { panelContentRefs.current[index] = element; }}
                className="native-v3-playoff-panel-content"
              >
                {panel}
              </div>
            </div>
          ))}
        </div>
      </div>
      <Dialog open={Boolean(infoType)} onOpenChange={(open) => { if (!open) setInfoType(null); }}>
        <DialogContent className="native-v3-playoff-info-dialog w-[calc(100%-2rem)] max-w-sm rounded-[1.2rem]">
          <DialogHeader>
            <DialogTitle>{PLAYOFF_INFO[infoType]?.title || "Slutspel"}</DialogTitle>
            <DialogDescription>{PLAYOFF_INFO[infoType]?.description || "Information om slutspelssteget."}</DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NativePlayoffs({ stages, loading, error, completedStep, currentStep }) {
  if (loading) {
    return (
      <div className="native-v3-playoffs-skeleton" aria-label="Laddar slutspel">
        <Skeleton className="native-v3-playoffs-steps-silhouette" />
        <Skeleton className="native-v3-playoffs-canvas-silhouette" />
      </div>
    );
  }
  if (error) return <div className="native-v3-standings-message">Kunde inte ladda slutspelet.</div>;
  return <PlayoffCanvas stages={stages} completedStep={completedStep} currentStep={currentStep} />;
}

export default function NativeStandingsPage({
  season,
  activeTab,
  onTabChange,
  rows,
  standingsLoading,
  standingsError,
  playoffStages,
  playoffsLoading,
  playoffsError,
  completedPlayoffStep,
  currentPlayoffStep,
  onNavigateTeam,
}) {
  const { data: account } = useAccount();
  const preferredId = String(account?.favorite_team_id || rows[0]?.team_id || rows[0]?.team || "");
  const [selectedId, setSelectedId] = React.useState(preferredId);
  const userSelectedTeamRef = React.useRef(false);

  React.useEffect(() => {
    const favoriteId = String(account?.favorite_team_id || "");
    if (!favoriteId || userSelectedTeamRef.current) return;
    if (rows.some((row) => String(row.team_id || row.team) === favoriteId)) {
      setSelectedId(favoriteId);
    }
  }, [account?.favorite_team_id, rows]);

  React.useEffect(() => {
    if (!rows.length) return;
    const selectedExists = rows.some((row) => String(row.team_id || row.team) === selectedId);
    if (!selectedExists) setSelectedId(preferredId || String(rows[0].team_id || rows[0].team));
  }, [preferredId, rows, selectedId]);

  const selectedTeam = rows.find((row) => String(row.team_id || row.team) === selectedId) || rows[0] || null;
  const selectTeam = React.useCallback((teamId) => {
    userSelectedTeamRef.current = true;
    setSelectedId(teamId);
  }, []);

  return (
    <div className="native-v3-standings-page">
      <StandingsHeader season={season} activeTab={activeTab} onTabChange={onTabChange} />
      {activeTab === "tabell" ? (
        <>
          {standingsLoading ? (
            <section className="native-v3-selected-team native-v3-selected-team-skeleton" aria-label="Laddar valt lag">
              <Skeleton className="native-v3-selected-team-silhouette" />
            </section>
          ) : <SelectedTeam team={selectedTeam} onOpenTeam={onNavigateTeam} />}
          <NativeTable rows={rows} loading={standingsLoading} error={standingsError} selectedId={selectedId} onSelect={selectTeam} />
        </>
      ) : (
        <NativePlayoffs
          stages={playoffStages}
          loading={playoffsLoading}
          error={playoffsError}
          completedStep={completedPlayoffStep}
          currentStep={currentPlayoffStep}
        />
      )}
    </div>
  );
}
